import { useState, useCallback, useEffect } from "react"
import { useDispatch } from "react-redux"
import { getChatSessions, getChatHistory } from "store/aiDashboard"
import { mapHistoryToMessages } from "../utils"

const SESSIONS_PAGE_SIZE = 10
const HISTORY_PAGE_SIZE = 50

export default function useChatSessions() {
  const dispatch = useDispatch()

  const [sessionsList, setSessionsList] = useState([])
  const [sessionsOffset, setSessionsOffset] = useState(0)
  const [sessionsTotal, setSessionsTotal] = useState(0)
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsError, setSessionsError] = useState(false)

  const [currentSessionId, setCurrentSessionId] = useState(null)
  const [sessionMessages, setSessionMessages] = useState({})
  const [sessionMeta, setSessionMeta] = useState({})
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState(null)

  const fetchSessions = useCallback((offset = 0, append = false) => {
    setSessionsLoading(true)
    setSessionsError(false)
    return dispatch(getChatSessions(SESSIONS_PAGE_SIZE, offset))
      .then((data) => {
        const incoming = data?.sessions ?? []
        setSessionsList((prev) => append ? [...prev, ...incoming] : incoming)
        setSessionsTotal(data?.total ?? 0)
        setSessionsOffset(offset)
      })
      .catch(() => setSessionsError(true))
      .finally(() => setSessionsLoading(false))
  }, [dispatch])

  useEffect(() => {
    fetchSessions(0)
  }, [fetchSessions])

  const loadMoreSessions = useCallback(() => {
    const nextOffset = sessionsOffset + SESSIONS_PAGE_SIZE
    if (nextOffset >= sessionsTotal) return
    fetchSessions(nextOffset, true)
  }, [fetchSessions, sessionsOffset, sessionsTotal])

  const selectSession = useCallback((sessionId) => {
    if (sessionId === currentSessionId) return
    setCurrentSessionId(sessionId)
    setHistoryError(null)

    if (sessionMessages[sessionId]) return

    setHistoryLoading(true)
    dispatch(getChatHistory(sessionId, HISTORY_PAGE_SIZE, 0))
      .then((data) => {
        const messages = mapHistoryToMessages(data?.messages ?? [])
        setSessionMessages((prev) => ({ ...prev, [sessionId]: messages }))
        setSessionMeta((prev) => ({ ...prev, [sessionId]: { total: data?.total ?? 0, offset: 0 } }))
      })
      .catch((err) => {
        const is404 = err?.status === 404
        if (is404) {
          setSessionsList((prev) => prev.filter((s) => s.session_id !== sessionId))
          setCurrentSessionId(null)
          setHistoryError(null)
        } else {
          setHistoryError("load_failed")
        }
      })
      .finally(() => setHistoryLoading(false))
  }, [dispatch, currentSessionId, sessionMessages])

  const loadOlderMessages = useCallback(() => {
    if (!currentSessionId) return
    const meta = sessionMeta[currentSessionId]
    if (!meta) return
    const nextOffset = meta.offset + HISTORY_PAGE_SIZE
    if (nextOffset >= meta.total) return

    setHistoryLoading(true)
    dispatch(getChatHistory(currentSessionId, HISTORY_PAGE_SIZE, nextOffset))
      .then((data) => {
        const older = mapHistoryToMessages(data?.messages ?? [])
        setSessionMessages((prev) => ({
          ...prev,
          [currentSessionId]: [...older, ...(prev[currentSessionId] ?? [])]
        }))
        setSessionMeta((prev) => ({
          ...prev,
          [currentSessionId]: { ...prev[currentSessionId], offset: nextOffset }
        }))
      })
      .catch(() => setHistoryError("load_older_failed"))
      .finally(() => setHistoryLoading(false))
  }, [dispatch, currentSessionId, sessionMeta])

  const startNewSession = useCallback(() => {
    setCurrentSessionId(null)
    setHistoryError(null)
  }, [])

  const refreshSessions = useCallback(() => {
    fetchSessions(0)
  }, [fetchSessions])

  const currentMessages = currentSessionId ? (sessionMessages[currentSessionId] ?? null) : null
  const currentMeta = currentSessionId ? (sessionMeta[currentSessionId] ?? null) : null
  const hasMoreOlderMessages = currentMeta ? (currentMeta.offset + HISTORY_PAGE_SIZE < currentMeta.total) : false
  const hasMoreSessions = sessionsOffset + SESSIONS_PAGE_SIZE < sessionsTotal

  return {
    sessionsList,
    sessionsLoading,
    sessionsError,
    hasMoreSessions,
    loadMoreSessions,
    currentSessionId,
    selectSession,
    startNewSession,
    currentMessages,
    historyLoading,
    historyError,
    hasMoreOlderMessages,
    loadOlderMessages,
    refreshSessions,
    retryFetchSessions: () => fetchSessions(0),
  }
}
